// PersonalInfoSection.jsx
import React from 'react';
import { Box, Typography, Grid, Divider, Paper } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import SchoolIcon from '@mui/icons-material/School';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import CakeIcon from '@mui/icons-material/Cake';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import WorkIcon from '@mui/icons-material/Work';

const PersonalInfoSection = ({ employee = {} }) => {
  // Add defensive programming to handle potential BSON objects and NaN values
  
  // Function to safely extract values and convert BSON objects to safe values
  const safeExtract = (value, fallback = null) => {
    if (value === null || value === undefined) return fallback;
    
    // Handle NaN values (this is the key fix!)
    if (typeof value === 'number' && isNaN(value)) {
      return fallback;
    }
    
    // Check if it's a BSON object
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      // Handle BSON date objects
      if (value.$date) {
        return new Date(value.$date).toISOString();
      }
      // Handle BSON number objects
      if (value.$numberDouble !== undefined) {
        const num = Number(value.$numberDouble);
        return isNaN(num) ? fallback : num;
      }
      if (value.$numberInt !== undefined) {
        const num = Number(value.$numberInt);
        return isNaN(num) ? fallback : num;
      }
      if (value.$numberLong !== undefined) {
        const num = Number(value.$numberLong);
        return isNaN(num) ? fallback : num;
      }
      // Handle BSON ObjectId
      if (value.$oid) {
        return value.$oid;
      }
    }
    
    return value;
  };
  
  // Function to safely process arrays that might contain BSON objects
  const safeProcessArray = (arr) => {
    if (!Array.isArray(arr)) return [];
    return arr.map(item => {
      if (typeof item === 'object' && item !== null) {
        const processed = {};
        for (const [key, value] of Object.entries(item)) {
          processed[key] = safeExtract(value, value);
        }
        return processed;
      }
      return safeExtract(item, item);
    });
  };
  
  // Safely extract all fields
  const address = safeExtract(employee.address, {});
  const email = safeExtract(employee.email);
  const phone = safeExtract(employee.phone);
  const birth_date = safeExtract(employee.birth_date);
  const spouse_name = safeExtract(employee.spouse_name);
  const spouse = safeExtract(employee.spouse, {});
  const relationship_status = safeExtract(employee.relationship_status);
  const children = safeProcessArray(employee.children || []);
  const education = safeProcessArray(employee.education || []);
  const emergency_contact = safeExtract(employee.emergency_contact, {});
  const start_date = safeExtract(employee.start_date);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  // Format address for display
  const formatAddress = (addr) => {
    if (!addr) return 'N/A';
    const parts = [];
    if (addr.street) parts.push(addr.street);
    if (addr.city && addr.state) parts.push(`${addr.city}, ${addr.state}`);
    if (addr.zip_code) parts.push(addr.zip_code);
    return parts.join('\n') || 'N/A';
  };

  // Calculate age from birthday
  const calculateAge = (birthday) => {
    if (!birthday) return null;
    try {
      const birthDate = new Date(birthday);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    } catch (error) {
      return null;
    }
  };

  // Get spouse birthday from spouse object
  const getSpouseBirthday = () => {
    if (spouse && spouse.birthday) {
      return spouse.birthday;
    }
    return null;
  };

  return (
    <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Personal Information
      </Typography>

      <Grid container spacing={2}>
        {/* Contact Information */}
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 2, 
              bgcolor: 'background.default',
              border: '1px solid',
              borderColor: 'divider',
              height: '100%'
            }}
          >
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
              Contact Details
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
              <EmailIcon sx={{ mr: 1, fontSize: '1.2rem', color: 'primary.main' }} />
              <Typography variant="body2">
                {email || 'No email available'}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
              <PhoneIcon sx={{ mr: 1, fontSize: '1.2rem', color: 'primary.main' }} />
              <Typography variant="body2">
                {phone || 'No phone available'}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
              <HomeIcon sx={{ mr: 1, fontSize: '1.2rem', color: 'primary.main' }} />
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {formatAddress(address)}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
              <CakeIcon sx={{ mr: 1, fontSize: '1.2rem', color: 'primary.main' }} />
              <Typography variant="body2">
                {formatDate(birth_date) || 'No birth date available'}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <WorkIcon sx={{ mr: 1, fontSize: '1.2rem', color: 'primary.main' }} />
              <Typography variant="body2">
                Started: {formatDate(start_date) || 'N/A'}
              </Typography>
            </Box>
          </Paper>
        </Grid>
        
        {/* Family Information */}
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 2, 
              bgcolor: 'background.default',
              border: '1px solid',
              borderColor: 'divider',
              height: '100%'
            }}
          >
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
              Family
            </Typography>
            
            {/* Relationship Status */}
            {relationship_status && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                <FamilyRestroomIcon sx={{ mr: 1, fontSize: '1.2rem', color: 'primary.main' }} />
                <Typography variant="body2">
                  Status: {relationship_status}
                </Typography>
              </Box>
            )}
            
            {/* Spouse Information */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
              <FamilyRestroomIcon sx={{ mr: 1, fontSize: '1.2rem', color: 'primary.main' }} />
              <Typography variant="body2">
                Spouse: {spouse_name || 'N/A'}
              </Typography>
            </Box>
            
            {spouse_name && getSpouseBirthday() && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5, ml: 3.5 }}>
                <CakeIcon sx={{ mr: 1, fontSize: '1.2rem', color: 'primary.main' }} />
                <Typography variant="body2">
                  Birthday: {formatDate(getSpouseBirthday())}
                </Typography>
              </Box>
            )}
            
            {/* Children Information */}
            {children && children.length > 0 ? (
              <Box sx={{ ml: 3.5 }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Children ({children.length}):
                </Typography>
                {children.map((child, index) => {
                  const age = calculateAge(child.birthday);
                  return (
                    <Box key={index} sx={{ ml: 1, mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        • {child.name}{age ? ` (${age} years old)` : ''}
                      </Typography>
                      <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                        Birthday: {formatDate(child.birthday)}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ ml: 3.5 }}>
                No children information available
              </Typography>
            )}
            
            <Divider sx={{ my: 1.5 }} />
            
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Emergency Contact
            </Typography>
            
            {emergency_contact ? (
              <Box sx={{ ml: 0.5 }}>
                <Typography variant="body2">
                  {emergency_contact.name}
                </Typography>
                <Typography variant="body2">
                  Relationship: {emergency_contact.relationship}
                </Typography>
                <Typography variant="body2">
                  Phone: {emergency_contact.phone}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" sx={{ ml: 0.5 }}>
                No emergency contact information available
              </Typography>
            )}
          </Paper>
        </Grid>
        
        {/* Education */}
        <Grid item xs={12}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 2, 
              bgcolor: 'background.default',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
              Education
            </Typography>
            
            {education && education.length > 0 ? (
              <Grid container spacing={2}>
                {education.map((edu, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <SchoolIcon sx={{ mr: 1, fontSize: '1.2rem', color: 'primary.main' }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {edu.degree_type || edu.degree}
                        </Typography>
                        <Typography variant="body2">
                          {edu.institution}
                          {edu.location && ` (${edu.location})`}
                        </Typography>
                        {(edu.major || edu.minor) && (
                          <Typography variant="body2">
                            {edu.major}{edu.minor && edu.minor !== 'NaN' ? `, Minor: ${edu.minor}` : ''}
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary">
                          Graduated: {formatDate(edu.graduation_date) || 'N/A'}
                        </Typography>
                        {edu.is_first_generation && (
                          <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 0.5, color: 'primary.main' }}>
                            First Generation Graduate
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2">
                No education information available
              </Typography>
            )}
          </Paper>
        </Grid>
        
      </Grid>
    </Box>
  );
};

export default PersonalInfoSection;
